import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowClockwise,
  Check,
  ClipboardText,
  Eye,
  Plus,
  StopCircle,
  X,
} from "@phosphor-icons/react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../../app/auth";
import {
  Drawer,
  DataFreshness,
  MetricStrip,
  Modal,
  SearchInput,
  StatusTag,
} from "../../components/Primitives";
import { QueryErrorState } from "../../components/States";
import { useToast } from "../../components/Toast";
import { formatDateTime, formatTaskType } from "../../lib/format";
import { useApiQuery } from "../../lib/query";
import type { Approval, Device, Task, TaskListResponse } from "../../lib/types";
import { TaskDetail } from "./TaskDetail";

const taskTypes = [
  "check_social_status",
  "capture_accessibility_snapshot",
  "refresh_app_inventory",
  "sync_contacts",
  "sync_chatrooms",
  "sync_call_logs",
  "sync_sms",
  "upload_logs",
];

interface SavedTaskView {
  id: string;
  label: string;
  query: string;
  status: string;
}

const savedTaskViewsKey = "xmirror.admin.task-views.v1";

function readSavedTaskViews(): SavedTaskView[] {
  try {
    const stored = window.localStorage.getItem(savedTaskViewsKey);
    return stored ? (JSON.parse(stored) as SavedTaskView[]) : [];
  } catch {
    return [];
  }
}

function CreateTaskModal({
  devices,
  initialDeviceIds = [],
  onClose,
}: {
  devices: Device[];
  initialDeviceIds?: string[];
  onClose(): void;
}) {
  const { request, user } = useAuth();
  const queryClient = useQueryClient();
  const notify = useToast();
  const [taskType, setTaskType] = useState(taskTypes[0]);
  const [selected, setSelected] = useState<string[]>(() => initialDeviceIds);
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [reason, setReason] = useState("");
  const mutation = useMutation({
    mutationFn: () =>
      request("/api/tasks", {
        method: "POST",
        body: JSON.stringify({
          tenantGuid: user?.tenantGuid,
          taskType,
          targetType: "device",
          targetDeviceIds: selected,
          payload: { source: "admin-web", requestedAt: new Date().toISOString() },
          requiresApproval,
          approvalReason: requiresApproval ? reason : undefined,
        }),
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["tasks"] }),
        queryClient.invalidateQueries({ queryKey: ["approvals"] }),
      ]);
      notify(
        requiresApproval ? "任务已提交审批" : "任务已进入派发队列",
        `${formatTaskType(taskType)} · ${selected.length} 台设备`,
      );
      onClose();
    },
  });

  const toggle = (id: string) =>
    setSelected((current) =>
      current.includes(id)
        ? current.filter((deviceId) => deviceId !== id)
        : [...current, id],
    );

  return (
    <Modal
      title="创建任务"
      description="选择任务类型和目标设备，可选审批后执行。"
      onClose={onClose}
      footer={
        <>
          <button className="secondary-button compact" onClick={onClose}>取消</button>
          <button
            className="primary-button"
            disabled={
              mutation.isPending ||
              !selected.length ||
              (requiresApproval && !reason.trim())
            }
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? "正在提交…" : requiresApproval ? "提交审批" : "立即下发"}
          </button>
        </>
      }
    >
      <div className="form-stack">
        <label>
          <span>任务类型</span>
          <select value={taskType} onChange={(event) => setTaskType(event.target.value)}>
            {taskTypes.map((type) => (
              <option key={type} value={type}>{formatTaskType(type)}</option>
            ))}
          </select>
        </label>
        <div>
          <div className="field-label">
            <span>目标设备</span>
            <button type="button" onClick={() => setSelected(devices.map((device) => device.id))}>
              全选
            </button>
          </div>
          <div className="device-picker">
            {devices.map((device) => (
              <label key={device.id}>
                <input
                  type="checkbox"
                  checked={selected.includes(device.id)}
                  onChange={() => toggle(device.id)}
                />
                <span>
                  <strong>{device.info?.model || `设备 #${device.id}`}</strong>
                  <small>{device.account?.username || device.info?.deviceSn || "未绑定"}</small>
                </span>
                <StatusTag value={device.status} />
              </label>
            ))}
            {!devices.length && <p className="inline-empty">当前没有可用设备。</p>}
          </div>
        </div>
        <label className="check-row">
          <input
            type="checkbox"
            checked={requiresApproval}
            onChange={(event) => setRequiresApproval(event.target.checked)}
          />
          <span><strong>需要审批后执行</strong><small>适用于影响范围较大的批量任务。</small></span>
        </label>
        {requiresApproval && (
          <label>
            <span>审批理由</span>
            <textarea
              rows={3}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="说明任务目的和影响范围"
            />
          </label>
        )}
        {mutation.isError && <p className="form-error">创建失败，请检查目标设备和权限。</p>}
      </div>
    </Modal>
  );
}

function TaskOperationModal({
  task,
  action,
  onClose,
}: {
  task: Task;
  action: "cancel" | "retry";
  onClose(): void;
}) {
  const { request } = useAuth();
  const queryClient = useQueryClient();
  const notify = useToast();
  const [reason, setReason] = useState("");
  const mutation = useMutation({
    mutationFn: () =>
      request(`/api/tasks/${task.id}/${action}`, {
        method: "POST",
        body: JSON.stringify({ reason }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["tasks"] });
      notify(action === "cancel" ? "任务已取消" : "任务已重新入队", `任务 #${task.id}`);
      onClose();
    },
  });
  return (
    <Modal
      title={action === "cancel" ? "取消任务" : "重试任务"}
      description={`${formatTaskType(task.taskType)} · 任务 #${task.id}`}
      onClose={onClose}
      footer={
        <>
          <button className="secondary-button compact" onClick={onClose}>返回</button>
          <button
            className={`primary-button ${action === "cancel" ? "danger-button" : ""}`}
            disabled={!reason.trim() || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? "正在处理…" : action === "cancel" ? "确认取消" : "确认重试"}
          </button>
        </>
      }
    >
      <div className="form-stack">
        <p className="operation-note">
          {action === "cancel"
            ? "仅未完成任务可取消；已经在设备执行的操作可能无法撤回。"
            : "仅失败或超时的设备结果会重新派发，已成功设备不会重复执行。"}
        </p>
        <label><span>操作原因</span><textarea rows={3} value={reason} onChange={(event) => setReason(event.target.value)} placeholder="必填，将写入审计日志" /></label>
        {mutation.isError && <p className="form-error">操作失败，当前任务状态可能不支持该操作。</p>}
      </div>
    </Modal>
  );
}

function ApprovalQueue({ approvals }: { approvals: Approval[] }) {
  const { request } = useAuth();
  const queryClient = useQueryClient();
  const notify = useToast();
  const review = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: "approved" | "rejected" }) =>
      request(`/api/approvals/${id}/review`, {
        method: "POST",
        body: JSON.stringify({ decision, comment: "由管理端任务中心审批" }),
      }),
    onSuccess: async (_, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["approvals"] }),
        queryClient.invalidateQueries({ queryKey: ["tasks"] }),
      ]);
      notify(variables.decision === "approved" ? "审批已通过" : "审批已拒绝");
    },
  });
  if (!approvals.length) return null;
  return (
    <section className="approval-panel">
      <header><div><h2>待审批任务</h2><p>审批通过后任务自动进入派发队列</p></div><span>{approvals.length}</span></header>
      <div className="approval-list">
        {approvals.map((approval) => (
          <div key={approval.id}>
            <ClipboardText size={20} />
            <span>
              <strong>{formatTaskType(approval.task?.taskType || approval.approvalType)}</strong>
              <small>{approval.reason || "未填写审批理由"} · {formatDateTime(approval.createdAt)}</small>
            </span>
            <button
              className="reject-action"
              disabled={review.isPending}
              onClick={() => review.mutate({ id: approval.id, decision: "rejected" })}
            ><X size={15} />拒绝</button>
            <button
              className="approve-action"
              disabled={review.isPending}
              onClick={() => review.mutate({ id: approval.id, decision: "approved" })}
            ><Check size={15} />通过</button>
          </div>
        ))}
      </div>
    </section>
  );
}

export function TasksPage() {
  const polling = Number(import.meta.env.VITE_QUERY_POLL_INTERVAL_MS || 10000);
  const { hasPermission } = useAuth();
  const [searchParams] = useSearchParams();
  const initialDeviceId = searchParams.get("deviceId");
  const [cursor, setCursor] = useState<string | undefined>();
  const [cursorHistory, setCursorHistory] = useState<Array<string | undefined>>([]);
  const [status, setStatus] = useState("all");
  const taskParams = new URLSearchParams({ limit: "25" });
  if (status !== "all") taskParams.set("status", status);
  if (cursor) taskParams.set("cursor", cursor);
  const tasksQuery = useApiQuery<TaskListResponse>(["tasks", status, cursor], `/api/tasks?${taskParams}`, {
    refetchInterval: polling,
  });
  const devicesQuery = useApiQuery<Device[]>(["devices", "task-picker"], "/api/devices?limit=200");
  const approvalsQuery = useApiQuery<Approval[]>(
    ["approvals", "pending"],
    "/api/approvals?status=pending&limit=50",
    { enabled: hasPermission("approvals:review"), refetchInterval: polling },
  );
  const [query, setQuery] = useState("");
  const [savedViews, setSavedViews] = useState<SavedTaskView[]>(readSavedTaskViews);
  const [createOpen, setCreateOpen] = useState(() => Boolean(initialDeviceId));
  const [selected, setSelected] = useState<Task | null>(null);
  const [operation, setOperation] = useState<{ task: Task; action: "cancel" | "retry" } | null>(null);
  const tasks = tasksQuery.data?.items || [];
  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return tasks.filter((task) => {
      const matchesQuery =
        !normalized ||
        `${task.id} ${task.taskType} ${formatTaskType(task.taskType)}`.toLowerCase().includes(normalized);
      return matchesQuery;
    });
  }, [query, tasks]);
  const active = tasks.filter((task) => ["created", "queued", "dispatching", "sent", "acknowledged", "running"].includes(task.status)).length;
  const succeeded = tasks.filter((task) => task.status === "succeeded").length;
  const failed = tasks.filter((task) => ["failed", "timeout"].includes(task.status)).length;
  const saveCurrentView = () => {
    const next: SavedTaskView = {
      id: `${status}:${query.trim().toLowerCase()}`,
      label: query.trim() ? `${status === "all" ? "全部状态" : status} · ${query.trim()}` : status === "all" ? "全部任务" : `状态：${status}`,
      query: query.trim(),
      status,
    };
    const updated = [...savedViews.filter((view) => view.id !== next.id), next].slice(-8);
    setSavedViews(updated);
    window.localStorage.setItem(savedTaskViewsKey, JSON.stringify(updated));
  };

  if (tasksQuery.isError || devicesQuery.isError) {
    return <QueryErrorState onRetry={() => { void tasksQuery.refetch(); void devicesQuery.refetch(); }} />;
  }

  return (
    <div className="module-page">
      <MetricStrip metrics={[
        { label: "任务总数", value: tasks.length, note: "保留完整派发历史" },
        { label: "进行中", value: active, note: "排队、派发或执行中", tone: "metric-blue" },
        { label: "已成功", value: succeeded, note: "全部目标执行成功", tone: "metric-green" },
        { label: "失败 / 超时", value: failed, note: "可按设备重试", tone: "metric-red" },
      ]} />
      {hasPermission("approvals:review") && <ApprovalQueue approvals={approvalsQuery.data || []} />}
      <section className="table-panel">
        <header className="module-toolbar">
          <div className="toolbar-left">
            <SearchInput value={query} onChange={setQuery} placeholder="搜索任务编号或类型" />
            <select value={status} onChange={(event) => { setStatus(event.target.value); setCursor(undefined); setCursorHistory([]); }}>
              <option value="all">全部状态</option>
              <option value="created">待审批</option>
              <option value="queued">排队中</option>
              <option value="running">执行中</option>
              <option value="succeeded">成功</option>
              <option value="failed">失败</option>
              <option value="timeout">超时</option>
              <option value="cancelled">已取消</option>
            </select>
            <select aria-label="已保存的任务筛选" defaultValue="" onChange={(event) => { const view = savedViews.find((item) => item.id === event.target.value); if (view) { setQuery(view.query); setStatus(view.status); setCursor(undefined); setCursorHistory([]); } }}>
              <option value="">已保存筛选</option>
              {savedViews.map((view) => <option key={view.id} value={view.id}>{view.label}</option>)}
            </select>
            <button className="text-button save-view" type="button" onClick={saveCurrentView}>保存当前筛选</button>
          </div>
          {hasPermission("tasks:create") && (
            devicesQuery.data?.length ? <button className="primary-button" onClick={() => setCreateOpen(true)}><Plus size={17} />创建任务</button> : <Link className="primary-button" to="/devices"><Plus size={17} />先连接设备</Link>
          )}
        </header>
        <div className="table-meta"><DataFreshness updatedAt={tasks[0]?.createdAt} isFetching={tasksQuery.isFetching} onRefresh={() => { void tasksQuery.refetch(); void approvalsQuery.refetch(); }} /></div>
        <div className="data-table-wrap">
          <table className="data-table task-table">
            <thead><tr><th>任务</th><th>状态</th><th>目标设备</th><th>结果</th><th>创建时间</th><th>操作</th></tr></thead>
            <tbody>
              {filtered.map((task) => {
                const successCount = task.results.filter((result) => result.status === "succeeded").length;
                const failureCount = task.results.filter((result) => ["failed", "timeout"].includes(result.status)).length;
                return (
                  <tr key={task.id} tabIndex={0} onClick={() => setSelected(task)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") setSelected(task); }}>
                    <td><span className="task-cell"><ClipboardText size={19} /><span><strong>{formatTaskType(task.taskType)}</strong><small>#{task.id}</small></span></span></td>
                    <td><StatusTag value={task.status} /></td>
                    <td>{task.results.length} 台</td>
                    <td><span className="result-count"><b>{successCount}</b> 成功 · <em>{failureCount}</em> 失败</span></td>
                    <td>{formatDateTime(task.createdAt)}</td>
                    <td onClick={(event) => event.stopPropagation()}>
                      <div className="row-actions">
                        <button title="查看详情" onClick={() => setSelected(task)}><Eye size={16} /></button>
                        {hasPermission("tasks:manage") && ["created", "queued", "dispatching", "sent", "acknowledged", "running"].includes(task.status) && (
                          <button title="取消" onClick={() => setOperation({ task, action: "cancel" })}><StopCircle size={16} /></button>
                        )}
                        {hasPermission("tasks:manage") && ["failed", "timeout"].includes(task.status) && (
                          <button title="重试" onClick={() => setOperation({ task, action: "retry" })}><ArrowClockwise size={16} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!tasksQuery.isPending && !filtered.length && <div className="action-empty"><ClipboardText size={30} /><strong>{devicesQuery.data?.length ? "还没有任务记录" : "连接设备后才能创建任务"}</strong><p>{devicesQuery.data?.length ? "创建一个设备任务，执行进度和结果会在这里持续更新。" : "先前往设备管理生成绑定码，再回到这里下发首个任务。"}</p>{hasPermission("tasks:create") && (devicesQuery.data?.length ? <button className="primary-button" onClick={() => setCreateOpen(true)}>创建首个任务</button> : <Link className="primary-button" to="/devices">前往设备管理</Link>)}</div>}
          {tasksQuery.isPending && <div className="table-empty">正在加载任务…</div>}
        </div>
        <footer className="pagination-bar"><span>每页最多 25 条 · 当前 {tasks.length} 条</span><div><button className="secondary-button compact" disabled={!cursorHistory.length || tasksQuery.isFetching} onClick={() => { const history = [...cursorHistory]; setCursor(history.pop()); setCursorHistory(history); }}>上一页</button><button className="secondary-button compact" disabled={!tasksQuery.data?.page.hasMore || !tasksQuery.data?.page.nextCursor || tasksQuery.isFetching} onClick={() => { setCursorHistory((current) => [...current, cursor]); setCursor(tasksQuery.data?.page.nextCursor); }}>下一页</button></div></footer>
      </section>
      {createOpen && Boolean(devicesQuery.data?.length) && <CreateTaskModal devices={devicesQuery.data || []} initialDeviceIds={initialDeviceId ? [initialDeviceId] : []} onClose={() => setCreateOpen(false)} />}
      {selected && <TaskDetail task={selected} onClose={() => setSelected(null)} />}
      {operation && <TaskOperationModal task={operation.task} action={operation.action} onClose={() => setOperation(null)} />}
    </div>
  );
}
